var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useRef, useMemo, useCallback, useState } from 'react';
import { useFusion } from '@rendering/fusion-react';
import { paint } from '@rendering/flash';
import { useCompiledScene } from '../../../hooks/useCompiledScene';
import { FusionSceneServerFallbackPreview } from './FusionSceneServerFallbackPreview';
import { selectPanelByIndex } from '../../../utils/cimDocSelectors';
function InnerFusionScenePreview({ cimDoc, compiledScene, user, view, width, children, onError, onFusionPaint, onPaint, fusionOptions, }) {
    const canvasRef = useRef(null);
    const [error, setError] = useState(undefined);
    const fusionDimension = useMemo(() => {
        if (compiledScene && compiledScene.documents[0].width) {
            const scalar = compiledScene.documents[0].width / compiledScene.width;
            const fusionWidth = width * scalar;
            return { width: Math.round(fusionWidth) };
        }
        // Placeholder value. Fusion won't draw until a selector is resolved, which will be there when there's a compiledScene
        return { width };
    }, [width, compiledScene]);
    const selectors = useMemo(() => {
        if (compiledScene) {
            if (view.scene.type === 'dynamic') {
                return [
                    {
                        type: 'panel',
                        id: selectPanelByIndex({ cimDoc, panelIndex: compiledScene.documents[0].page - 1 }).id,
                    },
                ];
            }
            return [
                {
                    type: 'page',
                    number: compiledScene.documents[0].page,
                },
            ];
        }
        return [];
    }, [compiledScene, view, cimDoc]);
    const onInnerError = useCallback((e) => {
        onError === null || onError === void 0 ? void 0 : onError(e);
        setError(e);
    }, [onError]);
    const onInnerPaint = useCallback((args) => {
        var _a;
        onFusionPaint === null || onFusionPaint === void 0 ? void 0 : onFusionPaint(args);
        const canvasContext = (_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getContext('2d');
        if (compiledScene && canvasContext) {
            const scalar = width / compiledScene.width;
            const height = compiledScene.height * scalar;
            try {
                paint({ document: args[0].canvas, dimensions: { width, height }, outputCtx: canvasContext, scene: compiledScene });
                onPaint === null || onPaint === void 0 ? void 0 : onPaint();
            }
            catch (e) {
                onInnerError === null || onInnerError === void 0 ? void 0 : onInnerError(e);
                return;
            }
        }
        // Clear previous error
        setError(undefined);
    }, [compiledScene, width, onPaint, onFusionPaint, onInnerError]);
    const { loading } = useFusion({
        cimDoc,
        dimension: fusionDimension,
        onError: onInnerError,
        onPaint: onInnerPaint,
        selectors,
        textOptions: fusionOptions.textOptions,
        referrer: user.referer,
        debugOptions: fusionOptions.debugOptions,
        experimentalOptions: fusionOptions.experimentalOptions,
    });
    const Canvas = useCallback((props) => React.createElement("canvas", Object.assign({ ref: canvasRef, style: { width: '100%' } }, props)), []);
    if (!children) {
        return React.createElement(Canvas, null);
    }
    return (React.createElement(React.Fragment, null, children({
        Canvas,
        status: {
            loading,
            error,
        },
    })));
}
export function FusionScenePreview(props) {
    var _a;
    const { compiledScene, needsServerFallback } = useCompiledScene({ scene: props.view.scene.url });
    const { cimDoc, transformCimDocForServerFallback } = props, rest = __rest(props, ["cimDoc", "transformCimDocForServerFallback"]);
    // Adds the ability to transform the cimDoc for server side rendering
    // For example: filter out client side images
    const serverRendableCimDoc = (_a = transformCimDocForServerFallback === null || transformCimDocForServerFallback === void 0 ? void 0 : transformCimDocForServerFallback(cimDoc)) !== null && _a !== void 0 ? _a : cimDoc;
    if (needsServerFallback) {
        return React.createElement(FusionSceneServerFallbackPreview, Object.assign({}, rest, { cimDoc: serverRendableCimDoc }));
    }
    return React.createElement(InnerFusionScenePreview, Object.assign({}, props, { compiledScene: compiledScene }));
}
//# sourceMappingURL=index.js.map